import React, { useState, useEffect } from 'react';
import { Box, Title, Table, Button, Badge, Group, Text, Alert, ActionIcon, Modal, TextInput, Textarea, NumberInput, FileInput, Stack } from '@mantine/core';
import { IconCheck, IconX, IconVideo, IconTrash, IconEdit } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.ailinkb.shop';

export default function MySkillsTab({ user }) {
  const { t } = useTranslation();
  const [skills, setSkills] = useState([]);
  const [error, setError] = useState('');
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editPrice, setEditPrice] = useState(9.99);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editPackageFile, setEditPackageFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.email) {
      fetchSkills();
    }
  }, [user]);

  const fetchSkills = () => {
    fetch(`${API_BASE_URL}/api/skills/my?ownerEmail=${encodeURIComponent(user.email)}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setSkills(data);
      })
      .catch(err => setError(err.toString()));
  };

  const deleteSkill = (id) => {
    if (!window.confirm(t('confirm_delete_skill', 'Czy na pewno chcesz usunąć ten skill ze sprzedaży?'))) return;
    
    fetch(`${API_BASE_URL}/api/skills/${id}?ownerEmail=${encodeURIComponent(user.email)}`, {
      method: 'DELETE'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          fetchSkills();
        } else {
          setError(data.error);
        }
      })
      .catch(err => setError(err.toString()));
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setEditTitle(skill.title || '');
    setEditDescription(skill.description || '');
    setEditTags(skill.tags || '');
    setEditPrice(skill.price || 9.99);
    setEditVideoFile(null);
    setEditPackageFile(null);
    setEditModalOpen(true);
  };

  const handleUpdate = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('title', editTitle);
    formData.append('description', editDescription);
    formData.append('tags', editTags);
    formData.append('price', editPrice);
    formData.append('ownerEmail', user.email);
    if (editPackageFile) formData.append('packageFile', editPackageFile);
    if (editVideoFile) formData.append('videoFile', editVideoFile);

    fetch(`${API_BASE_URL}/api/skills/${editingSkill.id}/update`, {
      method: 'PUT',
      body: formData
    })
    .then(res => res.json())
    .then(data => {
      setLoading(false);
      if (data.success) {
        setEditModalOpen(false);
        fetchSkills();
      } else {
        alert(data.error || t("generic_error", "Wystąpił błąd"));
      }
    })
    .catch(err => {
      setLoading(false);
      alert(t("connection_error", "Błąd połączenia: ") + err);
    });
  };

  return (
    <Box p="md">
      <Title order={3} mb="md">{t('my_published_skills', 'Moje wystawione Skille AI')}</Title>
      {error && <Alert color="red" mb="md">{error}</Alert>}
      
      {skills.length === 0 ? (
        <Text c="dimmed">{t('no_skills_yet', 'Nie wystawiłeś jeszcze żadnych skilli na sprzedaż.')}</Text>
      ) : (
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('skill_title', 'Tytuł')}</Table.Th>
              <Table.Th>{t('table_video', 'Wideo')} (URL)</Table.Th>
              <Table.Th>{t('skill_price', 'Cena (EUR)')}</Table.Th>
              <Table.Th>{t('table_status', 'Status')}</Table.Th>
              <Table.Th>{t('table_actions', 'Akcje')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {skills.map(skill => (
              <Table.Tr key={skill.id}>
                <Table.Td>{skill.title}</Table.Td>
                <Table.Td>
                  <a href={skill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${skill.videoUrl}` : skill.videoUrl} target="_blank" rel="noreferrer">
                    <Button variant="light" size="xs" leftSection={<IconVideo size={14} />}>{t('preview', 'Podgląd')}</Button>
                  </a>
                </Table.Td>
                <Table.Td>{skill.price}</Table.Td>
                <Table.Td>
                  {skill.status === 'APPROVED' && <Badge color="green">{t('status_approved', 'Opublikowany')}</Badge>}
                  {skill.status === 'PENDING' && <Badge color="yellow">{t('status_pending', 'Oczekuje na weryfikację')}</Badge>}
                  {skill.status === 'REJECTED' && <Badge color="red">{t('status_rejected', 'Odrzucony')}</Badge>}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon color="blue" variant="subtle" onClick={() => openEditModal(skill)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon color="red" variant="subtle" onClick={() => deleteSkill(skill.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title={t('edit_skill', 'Edytuj Skill')} size="lg">
        <Stack gap="md">
          <TextInput label={t("skill_title", "Tytuł")} value={editTitle} onChange={e => setEditTitle(e.target.value)} />
          <TextInput label={t("skill_tags", "Tagi")} value={editTags} onChange={e => setEditTags(e.target.value)} />
          <Textarea label={t("skill_description", "Opis")} value={editDescription} onChange={e => setEditDescription(e.target.value)} minRows={4} />
          <NumberInput label={t("skill_price", "Cena (EUR)")} min={0} value={editPrice} onChange={v => setEditPrice(v)} />
          
          <FileInput label={t("update_video_file", "Zaktualizuj wideo (opcjonalnie)")} accept="video/mp4" placeholder={t("click_to_select", "Kliknij by wybrać...")} value={editVideoFile} onChange={setEditVideoFile} clearable />
          <FileInput label={t("update_package_file", "Zaktualizuj paczkę (opcjonalnie)")} placeholder={t("click_to_select", "Kliknij by wybrać...")} value={editPackageFile} onChange={setEditPackageFile} clearable />
          
          <Button loading={loading} onClick={handleUpdate} color="blue" fullWidth>{t("save_changes", "Zapisz Zmiany")}</Button>
        </Stack>
      </Modal>
    </Box>
  );
}
